import type { ProblemLevel } from '@mathflat/domain/Problem'
import type { AllGradeSubject, Revision } from '@mathflat/domain/SchoolSystem'
import type { GroupStudentWorkbook } from '@mathflat/domain/StudentWorkbook'
import type { CustomSignatureWorkbook, Workbook } from '@mathflat/domain/Workbook'
import { tJosa } from '@mathflat/shared/i18n/tJosa'
import { addHours } from 'date-fns'

import ExtendedMap from '@/models/ExtendedMap'
import { commonWorkbookService } from '@/service/commonWorkbook.service'
import dateUtils from '@/utils/DateUtils'

import { DTO } from '../DTO'
import { SchoolGrade } from '../Student'

const now = new Date()
export class GroupWorkbook extends DTO {
  id: number //학생-교재 id
  type: Workbook['type'] //교재 타입 (PUBLIC: 시중, 교과서 , CUSTOM: 내 교재 )
  level: ProblemLevel //난이도
  // page: Workbook['page'] //총 페이지 수
  schoolGrade: SchoolGrade
  grade: AllGradeSubject
  revision: Revision
  semester: Workbook['semester']
  headtitle: string // 커스텀교재 - 학년학기
  title: string // 책 제목
  subtitle: string | null // 커스텀교재 - 소제목
  publisher: Workbook['publisher'] //출판사
  autoScored: boolean // 자동채점 여부
  pairFlag: CustomSignatureWorkbook['pairFlag'] // 쌍둥이 지원 여부
  answerPdfUrl: Workbook['answerPdfUrl'] //빠른 정답 url
  createDate: Date //생성일
  academyId?: string //생성 기관 ID(내 교재·커스텀 시그니처 교재)
  teacherId?: string //생성 선생님 ID(내 교재)
  // 최근 회차
  lastRound: NonNullable<GroupStudentWorkbook['lastRound']>
  recentAssignedDate: Date // 최근 출제 날짜
  roundToDetailMap: ExtendedMap<string, GroupStudentWorkbook['roundToDetailMap'][string]>

  pairX: 1 | 2 | 3 | null
  featureType?: CustomSignatureWorkbook['featureType']
  securingType: Workbook['securingType']
  originWorkbook: GroupStudentWorkbook['originWorkbook']
  answerConfirmed: boolean

  constructor({ workbook, ...data }: GroupStudentWorkbook) {
    super()
    this.id = workbook.id
    this.type = workbook.type
    this.level = workbook.level
    this.schoolGrade = new SchoolGrade({
      schoolType: workbook.schoolType,
      grade: Number(workbook.grade) as SchoolGrade['grade'],
    })
    this.grade = workbook.grade
    this.semester = workbook.semester
    this.revision = workbook.revision
    this.headtitle = workbook.headtitle
    this.subtitle = workbook.subtitle
    this.title = workbook.title
    this.publisher = workbook.publisher
    this.autoScored = workbook.autoScored
    this.pairFlag = 'pairFlag' in workbook && workbook.pairFlag
    this.answerPdfUrl = workbook.answerPdfUrl
    this.academyId = 'academyId' in workbook ? workbook.academyId : undefined
    this.teacherId = 'teacherId' in workbook ? workbook.teacherId : undefined
    this.createDate = new Date(workbook.createDatetime)
    // 회차가 하나도 없으면 서버가 null 을 준다. 새로 출제한 교재로 보이지 않도록 기원 시각으로 둔다
    this.recentAssignedDate = new Date(data.recentAssignedDatetime ?? 0)

    // 출제된 교재는 회차가 하나 이상이라 목록에서는 null 이 오지 않는다. 오더라도 첫 회차를 고른다
    this.lastRound = data.lastRound ?? 1
    this.roundToDetailMap = ExtendedMap.fromObj(data.roundToDetailMap)
    // 서버는 쌍둥이 배수를 1~3 으로만 낸다
    this.pairX = this.pairFlag ? (workbook.pairX as 1 | 2 | 3 | null) : null
    this.featureType = 'featureType' in workbook ? workbook.featureType : undefined
    this.securingType = workbook.securingType
    this.originWorkbook = data.originWorkbook
    this.answerConfirmed = 'answerConfirmed' in workbook && workbook.answerConfirmed
  }

  get formatDate() {
    return dateUtils.toDateString(this.createDate, '.')
  }

  get isAssignedOnlyOneRound() {
    return this.roundToDetailMap.size === 1
  }

  get isNewAssign() {
    return addHours(this.recentAssignedDate, 24) > now
  }

  get gradeSemesterSubject() {
    return SchoolGrade.getKoreanWorkbookGradeSemester({
      type: this.type,
      grade: this.grade,
      semester: this.semester,
      schoolType: this.schoolGrade.schoolType,
    })
  }

  get 한글학년학기과목() {
    return SchoolGrade.getKoreanWorkbookGradeSemester({
      type: this.type,

      grade: this.grade,
      semester: this.semester,
      schoolType: this.schoolGrade.schoolType,
    })
  }
  get isInspectBook() {
    return commonWorkbookService.checkInspectBook(this.id)
  }

  // 정책적으로 UI에 표시되어야 할 TITLE 값
  // subtitle의 유무에 따라서 title 또는 titel + subtitle로 노출 시킴
  get uiWorkbookTitle() {
    return `${this.title} ${this.subtitle ? `- ${this.subtitle}` : ''}`
  }
  get korAnswerConfirmed() {
    return this.answerConfirmed ? '지원' : '미지원'
  }
  get korWorkbookType() {
    switch (this.type) {
      case 'PUBLIC':
        return '시중교재'
      case 'CUSTOM':
        return '내 교재'
      case 'CUSTOM_SIGNATURE':
        return '시그니처 교재'
      case 'ACADEMY_GROUP':
        return tJosa('학원그룹교재')
      case 'SCHOOL':
        return '교과서'
      default:
        return ''
    }
  }
}
